import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, Menu, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const GlobalNavbar = () => {
  const { user, logout } = useAuth();
  const { currentLanguage, t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate(`/${currentLanguage}/system/login`);
  };

  const getPath = (path) => `/${currentLanguage}${path}`;

  return (
    <>
      {/* Gold accent line */}
      <div className="h-1 w-full bg-gradient-to-r from-[#FBBF24] via-[#FCD34D] to-[#FBBF24] relative z-[60]"></div>

      <nav
        className={`h-[68px] px-6 flex items-center justify-between sticky top-0 z-50 transition-all duration-300 ${scrolled
            ? 'bg-[#193D6D]/95 backdrop-blur-md shadow-xl shadow-[#193D6D]/10'
            : 'bg-[#193D6D] shadow-md'
          }`}
        aria-label="Navegação Principal"
        id="main-navigation"
      >
        {/* Logo */}
        <div className="flex items-center flex-shrink-0">
          <Link to={getPath('')} className="flex items-center no-underline transition-all duration-300 text-white hover:text-[#FCD34D] group" aria-label="PROVATIO - Página Inicial">
            <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center mr-3 group-hover:bg-[#FCD34D]/20 group-hover:border-[#FCD34D]/30 transition-all duration-300">
              <span className="text-[20px] leading-none" aria-hidden="true">🔬</span>
            </div>
            <span className="text-xl font-bold tracking-wider" style={{ fontFamily: "'Outfit', sans-serif" }}>PROVATIO</span>
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="md:hidden text-white p-2 hover:text-[#FCD34D] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          id="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop buttons */}
        <div className="hidden md:flex items-center gap-4 ml-auto">
          <LanguageSwitcher />

          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-white/70">{user.email?.split('@')[0]}</span>
              <Link
                to={getPath('/sistema')}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 bg-[#FCD34D] text-[#193D6D] hover:bg-[#FBBF24] hover:shadow-lg hover:shadow-[#FCD34D]/20 hover:-translate-y-0.5 tracking-wider uppercase"
                id="nav-system-btn"
              >
                <LayoutDashboard size={16} />
                SISTEMA
              </Link>
              <button
                onClick={handleLogout}
                className="p-2.5 rounded-full transition-all duration-300 text-white/60 hover:text-[#FCD34D] hover:bg-white/10"
                aria-label="Sair do sistema"
                id="nav-logout-btn"
              >
                <LogOut size={18} aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link
                to={getPath('/system/login')}
                className="text-sm font-semibold transition-all duration-300 whitespace-nowrap text-white/80 hover:text-white px-4 py-2"
                id="nav-login-link"
              >
                {t('navbar.entrar') || 'Entrar'}
              </Link>
              <Link
                to={getPath('/system/signup')}
                className="px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 bg-[#FCD34D] text-[#193D6D] hover:bg-[#FBBF24] hover:shadow-lg hover:shadow-[#FCD34D]/20 hover:-translate-y-0.5 whitespace-nowrap tracking-wider uppercase"
                id="nav-signup-btn"
              >
                {t('navbar.cadastrar') || 'Cadastrar'}
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-[#193D6D]/98 backdrop-blur-md flex flex-col items-center justify-center gap-6 animate-fade-in" style={{ top: 'calc(68px + 4px)' }}>
          <LanguageSwitcher />
          {user ? (
            <>
              <Link to={getPath('/sistema')} onClick={() => setMobileOpen(false)}
                className="px-8 py-3 rounded-full font-bold text-sm bg-[#FCD34D] text-[#193D6D] tracking-wider uppercase shadow-lg">
                <LayoutDashboard size={16} className="inline mr-2" />SISTEMA
              </Link>
              <button onClick={handleLogout} className="text-white/70 font-medium text-sm">Sair</button>
            </>
          ) : (
            <>
              <Link to={getPath('/system/login')} onClick={() => setMobileOpen(false)}
                className="text-white font-medium text-lg">Entrar</Link>
              <Link to={getPath('/system/signup')} onClick={() => setMobileOpen(false)}
                className="px-8 py-3 rounded-full font-bold text-sm bg-[#FCD34D] text-[#193D6D] tracking-wider uppercase shadow-lg">
                Cadastrar
              </Link>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default GlobalNavbar;